import React, { useEffect, useState } from 'react';
import Modal, { ModalActions, ModalContent, ModalTitle } from '../basic/Modal';
import ShotLocationPicker, { PickedLocation } from './ShotLocationPicker';
import { OUTCOME_STYLE, ShotChartRow, buildShotLocation, formatShotTime } from '../../utils/shotLocation';
import api from '../../config/axiosConfig';
import { API_URLS } from '../../config/Constants';
import { showErrorMsg, showSuccessMsg } from '../../config/common';

interface UnlocatedShotQueueProps {
  open: boolean;
  rows: ShotChartRow[]; // unlocated rows, in the order they should be placed
  clientId: string;
  seasonId: string;
  onClose: () => void;
  /** Called after each successful placement so the parent can update its own row list. */
  onLocated: (rowId: string, shotLocation: ReturnType<typeof buildShotLocation>) => void;
}

// Post-game "place missing shots" queue — the non-live counterpart to the
// hybrid capture flow. Coaches step through each unlocated shot (shooter,
// outcome, period, time) and tap the field once; skip moves on with no
// location placed.
const UnlocatedShotQueue: React.FC<UnlocatedShotQueueProps> = ({ open, rows, clientId, seasonId, onClose, onLocated }) => {
  const [index, setIndex] = useState(0);
  const [value, setValue] = useState<PickedLocation | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setIndex(0);
      setValue(null);
    }
  }, [open, rows.length]);

  if (!open) return null;

  const current = rows[index];
  const done = !current;

  const advance = () => {
    setValue(null);
    setIndex((i) => i + 1);
  };

  const handleSkip = () => advance();

  const handleSave = async () => {
    if (!current || !value) return;
    setSaving(true);
    try {
      const shotLocation = buildShotLocation(value.x, value.y, 'postgame');
      await api.post(API_URLS.UPDATE_STATS, {
        _id: current._id,
        clientId,
        seasonId,
        gameId: current.gameId,
        type: current.type,
        playerId: current.playerId,
        player: current.player,
        period: current.period,
        shotLocation,
      });
      onLocated(current._id, shotLocation);
      advance();
    } catch (err) {
      console.error('Error placing shot location:', err);
      showErrorMsg('Failed to save shot location. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (index > 0) showSuccessMsg(`Placed ${index} shot location${index === 1 ? '' : 's'}.`);
    onClose();
  };

  return (
        <Modal isOpen={open} onClose={handleClose} maxWidth="xl" closeOnBackdropClick={!saving}>
      <ModalTitle onClose={handleClose}>Place Missing Shots</ModalTitle>
      <ModalContent>
        {done ? (
          <div className="text-center py-8">
            <p className="text-lg font-semibold text-[#19133c] mb-2">All caught up!</p>
            <p className="text-sm text-gray-600">Every shot in this game has a location.</p>
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            <div className="text-xs font-semibold text-gray-500 text-center">
              Shot {index + 1} of {rows.length}
            </div>
            <div className="text-center">
              <span
                className="inline-block px-2 py-0.5 rounded-full text-xs font-bold text-white mb-1"
                style={{ backgroundColor: current.outcome ? OUTCOME_STYLE[current.outcome].color : '#255393' }}
              >
                {current.outcome ? OUTCOME_STYLE[current.outcome].label : current.type}
              </span>
              <p className="text-base font-semibold text-[#19133c]">
                {[
                  `${current.playerNumber ? `#${current.playerNumber} ` : ''}${current.playerName || ''}`.trim(),
                  current.period,
                  formatShotTime(current.time),
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </p>
            </div>
            <ShotLocationPicker
              outcome={current.outcome}
              value={value}
              onChange={setValue}
              title="Tap where this shot was taken"
              showSkip={false}
            />
          </div>
        )}
      </ModalContent>
      <ModalActions justify="between">
        {done ? (
          <button
            type="button"
            onClick={handleClose}
            className="w-full px-4 py-2 rounded-md bg-[#19133c] text-white font-semibold"
          >
            Close
          </button>
        ) : (
          <>
            <button
              type="button"
              onClick={handleSkip}
              disabled={saving}
              className="px-4 py-2 rounded-md border border-gray-300 text-gray-600 font-semibold disabled:opacity-50"
            >
              Skip
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving || !value}
              className="px-4 py-2 rounded-md bg-[#7fe526] text-black font-semibold disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Save & Next'}
            </button>
          </>
        )}
      </ModalActions>
    </Modal>
  );
};

export default UnlocatedShotQueue;
